import api from './api';

export const authService = {
  /**
   * Login user
   */
  async login(username, password) {
    const response = await api.post('/auth/login', { username, password });
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  /**
   * Logout user
   */
  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  },

  /**
   * Get current user
   */
  async getCurrentUser() {
    const response = await api.get('/me');
    return response.data;
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  },

  /**
   * Get stored user data
   */
  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
};

export const employmentService = {
  async getEmployment() {
    const response = await api.get('/employment');
    return response.data;
  },

  async createEmployment(data) {
    const response = await api.post('/employment', data);
    return response.data;
  },

  async updateEmployment(data) {
    const response = await api.patch('/employment', data);
    return response.data;
  },

  async uploadAttachments(files) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('category', 'employment');
    
    const response = await api.post('/employment/attachments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  async getAttachments() {
    const response = await api.get('/employment/attachments');
    return response.data;
  },

  async deleteAttachment(filename) {
    const response = await api.delete(`/employment/attachments/${filename}`);
    return response.data;
  }
};

export const workLogService = {
  async getWorkLogs() {
    const response = await api.get('/work-log');
    return response.data;
  },

  async createWorkLog(data) {
    const response = await api.post('/work-log', data);
    return response.data;
  },

  async updateWorkLog(id, data) {
    const response = await api.patch(`/work-log/${id}`, data);
    return response.data;
  },

  async deleteWorkLog(id) {
    const response = await api.delete(`/work-log/${id}`);
    return response.data;
  },

  async uploadEvidence(id, file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'worklog');
    
    const response = await api.post(`/work-log/${id}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  async deleteEvidence(id) {
    const response = await api.delete(`/work-log/${id}/evidence`);
    return response.data;
  }
};

export const reviewService = {
  async generateInviteLink() {
    const response = await api.post('/review/invite');
    return response.data;
  },

  async revokeInviteLink() {
    const response = await api.delete('/review/invite');
    return response.data;
  },

  async getReview() {
    const response = await api.get('/review');
    return response.data;
  },

  // Public endpoints (no auth required)
  // Note: These use axios directly to avoid /api prefix
  async getReviewForm(token) {
    const baseURL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3000';
    const response = await api.get(`/public/review/${token}`, {
      baseURL
    });
    return response.data;
  },

  async submitReview(token, data) {
    const baseURL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3000';
    const response = await api.post(`/public/review/${token}`, data, {
      baseURL
    });
    return response.data;
  }
};

export const reflectionService = {
  async getReflection() {
    const response = await api.get('/reflection');
    return response.data;
  },

  async updateReflection(data) {
    const response = await api.patch('/reflection', data);
    return response.data;
  }
};

export const dashboardService = {
  async getDashboard() {
    const response = await api.get('/dashboard');
    return response.data;
  },

  async updateProfile(data) {
    const response = await api.patch('/me', data);
    return response.data;
  },

  async uploadProfilePhoto(file) {
    const formData = new FormData();
    formData.append('photo', file);
    
    const response = await api.post('/me/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Education
  async getEducation() {
    const response = await api.get('/dashboard/education');
    return response.data;
  },

  async addEducation(data) {
    const response = await api.post('/dashboard/education', data);
    return response.data;
  },

  async updateEducation(id, data) {
    const response = await api.patch(`/dashboard/education/${id}`, data);
    return response.data;
  },

  async deleteEducation(id) {
    const response = await api.delete(`/dashboard/education/${id}`);
    return response.data;
  },

  async addQualification(educationId, data) {
    const response = await api.post(`/dashboard/education/${educationId}/qualification`, data);
    return response.data;
  },

  async deleteQualification(qualificationId) {
    const response = await api.delete(`/dashboard/qualification/${qualificationId}`);
    return response.data;
  },

  // Experience
  async getExperience() {
    const response = await api.get('/dashboard/experience');
    return response.data;
  },

  async addExperience(data) {
    const response = await api.post('/dashboard/experience', data);
    return response.data;
  },

  async updateExperience(id, data) {
    const response = await api.patch(`/dashboard/experience/${id}`, data);
    return response.data;
  },

  async deleteExperience(id) {
    const response = await api.delete(`/dashboard/experience/${id}`);
    return response.data;
  }
};

export const swotService = {
  async getSwots() {
    const response = await api.get('/swot');
    return response.data;
  },

  async createSwot(data) {
    const response = await api.post('/swot', data);
    return response.data;
  },

  async updateSwot(number, data) {
    const response = await api.patch(`/swot/${number}`, data);
    return response.data;
  },

  async updateReflection(reflection) {
    const response = await api.patch('/swot/reflection', { reflection });
    return response.data;
  }
};

export const pdpService = {
  async getTargets() {
    const response = await api.get('/pdp');
    return response.data;
  },

  async createTarget(data) {
    const response = await api.post('/pdp', data);
    return response.data;
  },

  async updateTarget(id, data) {
    const response = await api.patch(`/pdp/${id}`, data);
    return response.data;
  },

  async deleteTarget(id) {
    const response = await api.delete(`/pdp/${id}`);
    return response.data;
  }
};
